/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";
import { INSPECTOR_CONTROL_TYPE, TInspectorControl } from "./InspectorControlTypes";
import { InspectorControlString } from "./InspectorControlString";
import { InspectorControlBoolean } from "./InspectorControlBoolean";
import { InspectorControlInteger } from "./InspectorControlInteger";
import { InspectorControlFloat } from "./InspectorControlFloat";
import { InspectorControlPassword } from "./InspectorControlPassword";
import { InspectorControlEnum } from "./InspectorControlEnum";
import { InspectorControlExpression } from "./InspectorControlExpression";
import { InspectorControlCode } from "./InspectorControlCode";
import { InspectorControlFontSize } from "./InspectorControlFontSize";
import { InspectorControlValueType } from "./InspectorControlValueType";
import { InspectorControlActionSelector } from "./InspectorControlActionSelector";
import { InspectorControlViewSelector } from "./InspectorControlViewSelector";
import { InspectorControlIcon } from "./InspectorControlIcon";
import { InspectorControlSecret } from "./InspectorControlSecret";
import { InspectorControlAny } from "./InspectorControlAny";
import { InspectorControlColor } from "./InspectorControlColor";
import { InspectorControlBuilderAny } from "./InspectorControlBuilderAny";
import { InspectorControlComments } from "./InspectorControlComments";
import { InspectorControlResponsive } from "./InspectorControlResponsive";
import { InspectorControlRouteSelector } from "./InspectorControlRouteSelector";
import { InspectorControlSelect } from "./InspectorControlSelect";
import { InspectorControlButtonGroup } from "./InspectorControlButtonGroup";
import { InspectorControlExpand } from "./InspectorControlExpand";
import { InspectorControlAdd } from "./InspectorControlAdd";
import { InspectorControlThemeSelector } from "./InspectorControlThemeSelector";
import { InspectorControlIntegrationSelector } from "./InspectorControlIntegrationSelector";

export interface IInspectorControlProps {
	control: TInspectorControl;
}

export const InspectorControl: React.FC<IInspectorControlProps> = ({ control }) => {
	switch (control.type) {
		case INSPECTOR_CONTROL_TYPE.ACTION_SELECTOR:
			return <InspectorControlActionSelector model={control.model} />;
		case INSPECTOR_CONTROL_TYPE.ADD:
			return <InspectorControlAdd model={control.model} />;
		case INSPECTOR_CONTROL_TYPE.ANY:
			return <InspectorControlAny model={control.model} />;
		case INSPECTOR_CONTROL_TYPE.BOOLEAN:
			return <InspectorControlBoolean model={control.model} />;
		case INSPECTOR_CONTROL_TYPE.BUILDER_ANY:
			return <InspectorControlBuilderAny model={control.model} />;
		case INSPECTOR_CONTROL_TYPE.BUTTON_GROUP:
			return <InspectorControlButtonGroup model={control.model} />;
		case INSPECTOR_CONTROL_TYPE.CODE:
			return <InspectorControlCode model={control.model} language={control.language} options={control.options} />;
		case INSPECTOR_CONTROL_TYPE.COLOR:
			return <InspectorControlColor model={control.model} />;
		case INSPECTOR_CONTROL_TYPE.COMMENTS:
			return <InspectorControlComments model={control.model} />;
		case INSPECTOR_CONTROL_TYPE.ENUM:
			return <InspectorControlEnum model={control.model} />;
		case INSPECTOR_CONTROL_TYPE.EXPAND:
			return <InspectorControlExpand model={control.model} />;
		case INSPECTOR_CONTROL_TYPE.EXPRESSION:
			return <InspectorControlExpression model={control.model} />;
		case INSPECTOR_CONTROL_TYPE.FLOAT:
			return <InspectorControlFloat model={control.model} />;
		case INSPECTOR_CONTROL_TYPE.FONT_SIZE:
			return <InspectorControlFontSize model={control.model} />;
		case INSPECTOR_CONTROL_TYPE.ICON:
			return <InspectorControlIcon model={control.model} />;
		case INSPECTOR_CONTROL_TYPE.INTEGER:
			return <InspectorControlInteger model={control.model} />;
		case INSPECTOR_CONTROL_TYPE.INTEGRATION_SELECTOR:
			return <InspectorControlIntegrationSelector model={control.model} />;
		case INSPECTOR_CONTROL_TYPE.PASSWORD:
			return <InspectorControlPassword model={control.model} />;
		case INSPECTOR_CONTROL_TYPE.RESPONSIVE:
			return <InspectorControlResponsive model={control.model} />;
		case INSPECTOR_CONTROL_TYPE.ROUTE_SELECTOR:
			return <InspectorControlRouteSelector model={control.model} />;
		case INSPECTOR_CONTROL_TYPE.SECRET:
			return <InspectorControlSecret model={control.model} />;
		case INSPECTOR_CONTROL_TYPE.SELECT:
			return <InspectorControlSelect model={control.model} />;
		case INSPECTOR_CONTROL_TYPE.STRING:
			return <InspectorControlString model={control.model} multiline={control.multiline} />;
		case INSPECTOR_CONTROL_TYPE.THEME_SELECTOR:
			return <InspectorControlThemeSelector model={control.model} />;
		case INSPECTOR_CONTROL_TYPE.VALUE_TYPE:
			return <InspectorControlValueType models={control.models} />;
		case INSPECTOR_CONTROL_TYPE.VIEW_SELECTOR:
			return <InspectorControlViewSelector model={control.model} />;
		default:
			return null;
	}
};
